import { showKomboConnect, KomboConnectCancelledError } from '@kombo-api/connect';
import { Avatar, AvatarFallback, AvatarImage } from '@talentmesh/ui/components/avatar';
import { Button } from '@talentmesh/ui/components/button';
import { Card, CardContent } from '@talentmesh/ui/components/card';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { TypographyH4 } from '@talentmesh/ui/components/typography';
import { BookOpen } from 'lucide-react';
import { useState } from 'react';
import { useActivateAtsConnection, useCreateAtsConnectLink } from '../api/useAtsIntegration';
import { useNotificationContext } from '@/Context/NotificationContext';
import UIStrings from '@/Utils/UIStrings';

interface ConnectAtsCardProps {
    tool: string;
    label: string;
    logoUrl?: string | null;
    connectionGuideUrl?: string | null;
    onConnected: () => Promise<unknown> | void;
}

const ConnectAtsCard = ({ tool, label, logoUrl, connectionGuideUrl, onConnected }: ConnectAtsCardProps) => {
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const { trigger: createConnectLink, isMutating: creatingLink } = useCreateAtsConnectLink();
    const { trigger: activateConnection, isMutating: activating } = useActivateAtsConnection();
    const [isCreatingConnection, setIsCreatingConnection] = useState(false);

    const busy = isCreatingConnection || creatingLink || activating;

    const handleConnect = async () => {
        setIsCreatingConnection(true);
        try {
            // 1. Mint a short-lived Kombo Connect link for the chosen tool.
            const { link } = await createConnectLink(tool);

            // 2. Open the embedded Kombo Connect widget; it resolves with an activation token on success.
            const activationToken = await showKomboConnect(link);

            // 3. Exchange the activation token for the linked account and persist the connection.
            await activateConnection(activationToken);
            await onConnected();
            showSuccessToaster(UIStrings.AtsConnectedSuccessfully);
        } catch (error) {
            // The user closing the widget is not an error.
            if (error instanceof KomboConnectCancelledError) {
                return;
            }
            showFailToaster();
        } finally {
            setIsCreatingConnection(false);
        }
    };

    return (
        <Card className="w-full rounded-xl border p-4 drop-shadow-none">
            <CardContent
                className="flex h-full flex-col items-start justify-between gap-4 p-0"
                data-cy={`AtsIntegrationConnect-${tool}`}
            >
                <div className="flex flex-row items-center gap-2">
                    <Avatar className="size-10 rounded-full">
                        {logoUrl ? <AvatarImage src={logoUrl} alt={`${label} logo`} /> : null}
                        <AvatarFallback className="rounded-full">{label.slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    <TypographyH4 className="text-lg">{label}</TypographyH4>
                    {connectionGuideUrl && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon-xs"
                                        className="text-muted-foreground ml-auto size-6 shrink-0"
                                        aria-label={UIStrings.SetupGuide}
                                        asChild
                                    >
                                        <a href={connectionGuideUrl} target="_blank" rel="noopener noreferrer">
                                            <BookOpen className="size-4" />
                                        </a>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{UIStrings.SetupGuide}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={handleConnect}
                    className="h-auto w-full py-2 whitespace-normal"
                >
                    {busy && <Spinner className="mr-2 size-4" />}
                    {UIStrings.ConnectAts(label)}
                </Button>
            </CardContent>
        </Card>
    );
};

export default ConnectAtsCard;
